import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { ArrowLeft, Flask, Trash, TreeStructure, FileText } from '@phosphor-icons/react'
import { istDateKey } from '@/lib/ist'
import { useAuth } from '@/auth/context'
import { hasAnyRole, ROLES } from '@/auth/roles'
import { Input } from '@/ui'
import { errorMessage } from '../../api/core'
import { useExceptions, type ExceptionRow } from '../../api/exceptions'
import { useLot } from '../../api/gate'
import {
  useHolds,
  useLabReport,
  useLiftLabHolds,
  useTraceBackSamples,
  type HeldItem,
  type TraceBackLot,
} from '../../api/quality'
import { useOrgs } from '../../api/reference'
import { useDispatchSample, useSamples } from '../../api/store'
import { ActionButton } from '../../components/Can'
import { RefLink } from '../../components/bits'
import {
  BoardColumns,
  BoardPage,
  BoardTable,
  FactRow,
  Facts,
  LinkButton,
  Note,
  Pill,
  SidePanel,
  Tag,
  TwoLine,
  type BoardColumn,
} from '../../components/board'
import { useUrlState } from '../../lib/useUrlState'
import { boardDate, boardWhen } from '../../model/payoutBoard'
import { packLotState } from '../../model/packing'
import { problemBlocks, problemWords } from '../../model/release'
import { BoardCard } from '../packing/shared'
import { SignFields } from './shared'

const UNIT_KIND: Record<string, string> = {
  LOT: 'farmer lot',
  BATCH: 'brand batch',
  PACK: 'pack lot',
  BYPRODUCT: 'by-product',
  ORPHAN: 'sack',
}

/** Problems on the pack chain shown under the lab fail (the rest live on Exceptions). */
const CHAIN_TYPES = new Set([
  'TAG_CONFLICT',
  'TAG_REUSED',
  'RUN_MIXED',
  'BALANCE_OUT_OF_BAND',
  'LABEL_MISMATCH',
  'OTHER',
  'CUSTODY_RULE',
])

const daysOpen = (at: string | null) =>
  at ? Math.max(0, Math.floor((Date.now() - new Date(at).getTime()) / 86_400_000)) : null

/**
 * Lab holds (alignment map V1-F7-B1a AutoHold; F7-B1.a): the system holds the failing family and
 * the farmer's other lots at once and opens one critical problem; nobody decided it. Each open
 * lab fail shows the failing rows, what is held and where, and leads to Trace back and Dispose.
 */
export function HoldsPage() {
  const [f, setF] = useUrlState({ open: '' })
  const fails = useExceptions({ type: 'LAB_FAIL', status: 'OPEN', page: 1, limit: 50 })
  const others = useExceptions({ status: 'OPEN', page: 1, limit: 100 })
  const list = fails.data?.rows ?? []
  const selected = list.find((e) => e.id === f.open) ?? list[0] ?? null
  const chain = (others.data?.rows ?? []).filter(
    (e) => e.type !== 'LAB_FAIL' && CHAIN_TYPES.has(e.type),
  )
  const otherCols: BoardColumn<ExceptionRow>[] = [
    {
      id: 'what',
      header: 'Problem',
      cell: (e) => (
        <TwoLine
          top={problemWords(e.type)}
          sub={typeof e.detail.reason === 'string' ? problemWords(e.detail.reason) : undefined}
        />
      ),
    },
    { id: 'on', header: 'On', cell: (e) => <SubjectCode e={e} /> },
    {
      id: 'effect',
      header: 'Effect',
      cell: (e) =>
        problemBlocks(e.type, e.detail) ? (
          <Tag tone="bad">Blocks release</Tag>
        ) : (
          <Tag tone="plain">Note only</Tag>
        ),
    },
    {
      id: 'age',
      header: 'Open for',
      align: 'right',
      cell: (e) => `${daysOpen(e.raisedAt) ?? '—'} days`,
    },
  ]
  return (
    <BoardPage title="Lab holds">
      {fails.isLoading ? (
        <Note>Loading…</Note>
      ) : fails.error ? (
        <Note className="text-danger">{errorMessage(fails.error)}</Note>
      ) : list.length === 0 ? (
        <Pill tone="ok">No open lab fail. Nothing is held by a lab result.</Pill>
      ) : (
        <>
          {list.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {list.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => setF({ open: e.id })}
                  className={`rounded-chip border-[1.5px] px-3 py-1 text-[14px] font-bold ${selected?.id === e.id ? 'border-ink bg-paper-tint' : 'border-[#8a827a] bg-surface'}`}
                >
                  <SubjectCode e={e} />
                </button>
              ))}
            </div>
          )}
          {selected && <LabFail e={selected} />}
        </>
      )}
      <BoardCard title="Other open problems">
        <BoardTable<ExceptionRow>
          caption="Other open problems"
          columns={otherCols}
          rows={chain}
          getRowId={(e) => e.id}
          loading={others.isLoading}
          empty="None open on the pack chain."
        />
        <Note>
          Only problems that touch the four guarantees block a release in V1; the rest are listed.
        </Note>
      </BoardCard>
    </BoardPage>
  )
}

function SubjectCode({ e }: { e: ExceptionRow }) {
  const unit = useLot(e.subjectType === 'UNIT' ? e.subjectId : undefined)
  if (e.subjectType !== 'UNIT') return <>{e.subjectType.toLowerCase()}</>
  return (
    <>
      {unit.data
        ? `${unit.data.code}${unit.data.farmerName ? ` (${unit.data.farmerName})` : ''}`
        : '…'}
    </>
  )
}

function LabFail({ e }: { e: ExceptionRow }) {
  const unit = useLot(e.subjectId)
  const holds = useHolds(e.subjectId)
  const reportId = typeof e.detail.lab_report_id === 'string' ? e.detail.lab_report_id : null
  const report = useLabReport(reportId)
  const held = holds.data?.held ?? []
  const family = held.filter((h) => h.scope !== 'SIBLING')
  const siblings = held.filter((h) => h.scope === 'SIBLING')
  const failing = (report.data?.results ?? []).filter((r) => r.flag !== 'PASS')
  const code = unit.data?.code ?? '…'
  const cols: BoardColumn<HeldItem>[] = [
    {
      id: 'item',
      header: 'Held',
      cell: (h) => (
        <TwoLine
          top={h.packLotCode ?? h.code}
          sub={UNIT_KIND[h.unitType] ?? h.unitType.toLowerCase()}
        />
      ),
    },
    { id: 'holds', header: 'Holds', cell: (h) => <HeldWhat h={h} /> },
    { id: 'where', header: 'Where', cell: (h) => h.locationCode ?? '—' },
  ]
  return (
    <BoardColumns
      main={
        <>
          <BoardCard>
            <div className="flex flex-col gap-1">
              <b className="text-[18px]">
                Lab fail · critical · {code}
                {unit.data?.farmerName ? ` (${unit.data.farmerName})` : ''}
              </b>
              <span className="text-[14px] text-muted">
                Opened automatically {boardWhen(e.raisedAt)} · nobody decided this
              </span>
            </div>
            <Pill tone="bad">Hold applied to {held.length} items</Pill>
            {failing.map((r) => (
              <div key={`${r.panel}-${r.analyte}`} className="flex flex-col">
                <b>
                  {r.analyte} {r.value ?? 'detected'} {r.uom}
                </b>
                <span className="text-[14px] text-muted">
                  LOQ {r.loq} · limit {r.limitValue ?? '—'} · report {report.data?.reportNo} · test
                  date {boardDate(report.data?.testedAt)}
                </span>
              </div>
            ))}
          </BoardCard>
          <BoardCard title="Same family" aside={<Pill tone="bad">Held</Pill>}>
            <BoardTable<HeldItem>
              caption="Same family, held"
              columns={cols}
              rows={family}
              getRowId={(h) => h.unitId}
              loading={holds.isLoading}
              empty="Nothing held."
            />
          </BoardCard>
          <BoardCard
            title="The farmer’s other lots · until the gate sample is re-tested"
            aside={<Pill tone="bad">Held</Pill>}
          >
            <BoardTable<HeldItem>
              caption="Other lots, held"
              columns={cols}
              rows={siblings}
              getRowId={(h) => h.unitId}
              loading={holds.isLoading}
              empty="No other lots of this farmer."
            />
          </BoardCard>
          <Note>
            Nothing held can be allocated, released or dispatched, and its QR shows the on-hold
            page. A pack run still open on a held batch is stopped on the operator’s phone.
          </Note>
        </>
      }
      side={
        <SidePanel label="Lab fail" title={code} sub="What to do next">
          <Note className="text-ink">
            Trace back sends the farmer’s sealed gate sample and a fresh sample of the batch to the
            lab. When the fail is confirmed (or the re-test waived), dispose of the held stock.
          </Note>
          <div className="flex-1" />
          <div className="flex flex-col gap-2 [&>*]:w-full">
            <LinkButton
              to={`/c/quality/holds/${e.subjectId}/trace-back`}
              primary
              icon={<TreeStructure aria-hidden="true" />}
            >
              Trace back
            </LinkButton>
            {reportId && (
              <LinkButton
                to={`/c/quality/lab-results?open=${reportId}`}
                icon={<FileText aria-hidden="true" />}
              >
                Open report
              </LinkButton>
            )}
            <LinkButton
              to={`/c/quality/holds/${e.subjectId}/dispose?case=${e.id}`}
              icon={<Trash aria-hidden="true" />}
            >
              Dispose
            </LinkButton>
          </div>
          <LiftHolds unitIds={[e.subjectId, ...held.filter((h) => h.unitType !== 'PACK').map((h) => h.unitId)]} />
        </SidePanel>
      }
    />
  )
}

/**
 * Lift (F7-B1.a-c, units:lift-lab-hold): the farmer's other lots once their gate sample re-tests
 * PASS (verified), the failing family only once the failing report is replaced by the lab's
 * corrected report. The server decides per unit; what it keeps held is listed with the reason.
 * Pack lots are never lifted here — each needs its own release.
 */
function LiftHolds({ unitIds }: { unitIds: string[] }) {
  const lift = useLiftLabHolds()
  const [reason, setReason] = useState('')
  const [password, setPassword] = useState('')
  const refused = Object.entries(lift.data?.refused ?? {})
  const blocked =
    reason.trim().length < 10 ? 'Say why (at least 10 characters)' : !password ? 'Enter your password' : null
  return (
    <div className="flex flex-col gap-2 border-t border-rule pt-3">
      <b className="text-[15px]">Lift the hold after a passing re-test</b>
      <label className="flex flex-col gap-1 text-[15px]">
        <span className="font-bold">Why</span>
        <Input value={reason} onChange={(ev) => setReason(ev.target.value)} placeholder="Gate sample re-test LR-… verified PASS" />
      </label>
      <SignFields password={password} onPassword={setPassword} />
      <ActionButton
        perm="units:lift-lab-hold"
        blockedReason={blocked}
        loading={lift.isPending}
        onClick={() =>
          void lift
            .mutateAsync({ unitIds: [...new Set(unitIds)], reason: reason.trim(), password })
            .finally(() => setPassword(''))
        }
      >
        Lift hold
      </ActionButton>
      {refused.length > 0 && (
        <Note>
          Still held: {refused.length} item{refused.length === 1 ? '' : 's'} (
          {[...new Set(refused.map(([, why]) => why.toLowerCase().replace(/_/g, ' ')))].join('; ')}).
        </Note>
      )}
    </div>
  )
}

function HeldWhat({ h }: { h: HeldItem }) {
  if (h.unitType === 'PACK') {
    const s = packLotState(h.packLotStatus ?? '', h.packLotHold ?? 'NONE')
    return (
      <span className="flex flex-col">
        <span>
          {h.packLotStatus === 'PACKING'
            ? 'pack run, still open'
            : `${h.pouchCount ?? '—'} pouches`}
        </span>
        <span className="text-[13px] text-muted">{s.text}</span>
      </span>
    )
  }
  return (
    <span>
      {h.hold === 'NONE' ? 'used up' : h.holdReason?.toLowerCase().replace(/_/g, ' ') || 'held'}
    </span>
  )
}

// ============================== trace back (F7-B1.b) ==============================

/**
 * Trace back · <batch> (alignment map V1-F7-B1b TraceBack; F7-B1.b): the farmer's lots with their
 * sealed gate samples; send the gate sample and a freshly sealed batch sample to the one contracted
 * lab. Results are attached as in F7.5.
 */
export function TraceBackPage() {
  const { roles } = useAuth()
  // farmers/:id is a money page (MONEY in routes.tsx): the link only for those who may open it.
  const canOpenFarmer = hasAnyRole(roles, [ROLES.PAYMENTS_APPROVER, ROLES.SUPERVISOR])
  const { unitId } = useParams()
  const unit = useLot(unitId)
  const tb = useTraceBackSamples(unitId)
  const holds = useHolds(unitId)
  const labs = useOrgs('LAB')
  const lab = labs.data?.length === 1 ? labs.data[0] : null
  const qc = useSamples({
    unitId: unitId ?? '00000000-0000-0000-0000-000000000000',
    kind: 'BATCH_QC',
    page: 1,
    limit: 10,
  })
  const siblings = (holds.data?.held ?? []).filter(
    (h) => h.scope === 'SIBLING' && h.unitType === 'LOT',
  )
  const lots = tb.data?.lots ?? []
  const gate = lots[0] ?? null
  const confirming =
    (qc.data?.rows ?? []).find((s) => s.status === 'SEALED' || s.status === 'IN_STORE') ?? null
  const cols: BoardColumn<TraceBackLot | HeldItem>[] = [
    {
      id: 'lot',
      header: 'Farmer lot',
      cell: (l) => (
        <TwoLine top={l.code} sub={'scope' in l ? 'held · her other lot' : 'this batch'} />
      ),
    },
    {
      id: 'seal',
      header: 'Gate sample seal',
      cell: (l) => ('sample' in l ? (l.sample?.seal ?? 'none') : '—'),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (l) =>
        'sample' in l ? (
          l.sample ? (
            <Pill tone={l.sampleUsable ? 'ok' : 'bad'}>
              {l.sampleUsable ? humanSample(l.sample.status) : 'Missing or past keep-until'}
            </Pill>
          ) : (
            <Pill tone="bad">No gate sample</Pill>
          )
        ) : (
          <RefLink to={`/c/lots/${l.unitId}`}>Open lot</RefLink>
        ),
    },
    {
      id: 'keep',
      header: 'Keep until',
      hideBelow: 'sm',
      cell: (l) => ('sample' in l ? boardDate(l.sample?.retainUntil) : '—'),
    },
  ]
  return (
    <BoardPage
      title={`Trace back · ${unit.data?.code ?? '…'}`}
      actions={
        <>
          <LinkButton to={`/c/quality/holds/${unitId}/dispose`} icon={<Trash aria-hidden="true" />}>
            Dispose
          </LinkButton>
          <LinkButton to="/c/quality/holds" icon={<ArrowLeft aria-hidden="true" />}>
            Lab holds
          </LinkButton>
        </>
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        <Pill tone="bad">On hold · lab fail</Pill>
        <span className="text-[15px]">
          {unit.data?.farmerName ?? 'Farmer'}
          {unit.data?.rosterNo ? ` · roster no. ${unit.data.rosterNo}` : ''} · her lots
        </span>
        {canOpenFarmer && unit.data?.farmerId && (
          <RefLink to={`/c/farmers/${unit.data.farmerId}`}>Open farmer</RefLink>
        )}
      </div>
      {tb.error && <Note className="text-danger">{errorMessage(tb.error)}</Note>}
      {tb.data && tb.data.farmersCount > 1 && (
        <Pill tone="bad" wrap>
          More than one farmer upstream of this batch: a critical problem is open (one-farmer rule).
        </Pill>
      )}
      <BoardTable<TraceBackLot | HeldItem>
        caption="Her lots"
        columns={cols}
        rows={[...lots, ...siblings.filter((s) => !lots.some((l) => l.unitId === s.unitId))]}
        getRowId={(l) => l.unitId}
        loading={tb.isLoading}
        empty="No farmer lot found upstream."
      />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <SendCard
          title={gate?.sample?.seal ? `Gate sample · seal ${gate.sample.seal}` : 'Gate sample'}
          what="Shows whether the residue came with the crop; its result also decides when her other lots can be freed."
          facts={[
            [
              'Taken at the gate',
              gate
                ? `${gate.code}${gate.sample?.status ? ` · ${humanSample(gate.sample.status).toLowerCase()}` : ''}`
                : '—',
            ],
            ['Lab', lab ? `${lab.name} · contracted` : labs.isLoading ? '…' : 'No lab set up'],
          ]}
          sampleId={gate?.sampleUsable ? (gate.sample?.id ?? null) : null}
          sampleStatus={gate?.sample?.status ?? null}
          labOrgId={lab?.id ?? null}
          button="Send gate sample"
        />
        <SendCard
          title={
            confirming?.sealPayload
              ? `Confirming sample · new QC seal ${confirming.sealPayload}`
              : 'Confirming sample'
          }
          what="A fresh sample from the held batch, to confirm the fail. It is sealed on the phone."
          facts={[
            [
              'Sealed on the phone',
              confirming
                ? `${boardWhen(confirming.collectedAt)}${confirming.weightG ? ` · ${confirming.weightG} g` : ''}`
                : 'not yet',
            ],
            ['Lab', lab ? `${lab.name} · contracted` : 'No lab set up'],
          ]}
          sampleId={confirming?.id ?? null}
          sampleStatus={confirming?.status ?? null}
          labOrgId={lab?.id ?? null}
          button="Send confirming sample"
        />
      </div>
      <BoardCard title="What the two results will tell us">
        <ul className="flex flex-col gap-1.5 text-[15px]">
          <li>
            <b>Gate clean, batch fails again:</b> the residue got in after the farm gate: check the
            store and machine records for that time (F5, F6). Her other lots can be freed.
          </li>
          <li>
            <b>Both fail:</b> it came with the crop. Her other lots stay held.
          </li>
          <li>
            <b>Confirming sample clean:</b> the quality person decides with the lab what happens
            next.
          </li>
        </ul>
        <Note>
          Gate sample past its keep-until date or missing: a problem is recorded and the confirming
          test still goes ahead. Both samples go to the one contracted lab; results are attached as
          in F7.5.
        </Note>
        <LinkButton to="/c/quality/lab-results" icon={<Flask aria-hidden="true" />}>
          Attach a result
        </LinkButton>
      </BoardCard>
    </BoardPage>
  )
}

const humanSample = (s: string) =>
  ({
    SEALED: 'Sealed',
    IN_STORE: 'In store',
    SENT_TO_LAB: 'Sent to lab',
    TESTED: 'Tested',
    DISPOSED: 'Disposed',
  })[s] ?? s

function SendCard({
  title,
  what,
  facts,
  sampleId,
  sampleStatus,
  labOrgId,
  button,
}: {
  title: string
  what: string
  facts: Array<[string, string]>
  sampleId: string | null
  sampleStatus: string | null
  labOrgId: string | null
  button: string
}) {
  const send = useDispatchSample()
  const [error, setError] = useState<string | null>(null)
  const sent = sampleStatus === 'SENT_TO_LAB' || sampleStatus === 'TESTED'
  const blocked = !sampleId
    ? 'No usable sample yet.'
    : !labOrgId
      ? 'No contracted lab is set up.'
      : sent
        ? 'Already sent.'
        : null
  return (
    <BoardCard title={title}>
      <Note className="text-ink">{what}</Note>
      <Facts>
        {facts.map(([k, v]) => (
          <FactRow key={k} label={k}>
            {v}
          </FactRow>
        ))}
      </Facts>
      {sent && <Pill tone="ok">{humanSample(sampleStatus!)}</Pill>}
      {error && <Note className="text-danger">{error}</Note>}
      <ActionButton
        perm="samples:dispatch"
        variant="secondary"
        blockedReason={blocked}
        loading={send.isPending}
        onClick={() => {
          setError(null)
          void send
            .mutateAsync({
              id: sampleId!,
              labOrgId: labOrgId!,
              panelRequested: 'PESTICIDES',
              sentAt: istDateKey(),
            })
            .catch((e: unknown) => setError(errorMessage(e)))
        }}
      >
        {button}
      </ActionButton>
    </BoardCard>
  )
}
